import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { CodingFieldAdapter } from 'components/Assessments/PatAssessment/adapters/CodingFieldAdapter'
import { CNASkilledTasksFormField } from './formField'

/**
 * CNA Skilled Tasks subsection adapter for PAT Assessment
 * Handles CNA skilled tasks extraction and transformation
 */
export class CNASkilledTasksAdapter extends CodingFieldAdapter {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract coding values for questions using helper method
    const bathing = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BATHING,
      false
    )
    const bracesSplintsStockings = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS,
      false
    )
    const bracesSplintsStockingsAdditional = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL,
      false
    )
    const dressing = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING,
      false
    )
    const eating = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_EATING,
      false
    )
    const tubeFeedings = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS,
      false
    )
    const medication = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION,
      false
    )
    const mobility = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY,
      false
    )
    const mobilityAdditional = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL,
      false
    )
    const mealPrep = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP,
      false
    )
    const grooming = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING,
      false
    )
    const groomingAdditional = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL,
      false
    )
    const skinCareApplication = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION,
      false
    )
    const skinCareApplicationAdditional = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL,
      false
    )
    const skinCare = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE,
      false
    )
    const skinCareAdditional = this.extractCodingArray(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL,
      false
    )
    const romExercise = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE,
      false
    )
    const respiratoryCare = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE,
      false
    )
    const oxygenTherapy = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY,
      false
    )
    const oralSuctioning = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING,
      false
    )
    const bowelFrequency = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY,
      false
    )
    const bowelHygiene = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE,
      false
    )
    const bowelElimination = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION,
      false
    )
    const bowelProgram = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM,
      false
    )
    const catheterCare = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE,
      false
    )
    const menses = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MENSES,
      false
    )
    const urinaryHygiene = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE,
      false
    )
    const positioning = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING,
      false
    )
    const transfers = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS,
      false
    )
    const vitalsPhysician = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN,
      false
    )
    const vitalsNurse = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE,
      false
    )

    // Extract notes fields using helper method
    const bathingNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BATHING_NOTES
    )
    const bracesSplintsStockingsNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_NOTES
    )
    const dressingNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING_NOTES
    )
    const eatingNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_EATING_NOTES
    )
    const tubeFeedingsNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS_NOTES
    )
    const medicationNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION_NOTES
    )
    const mobilityNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_NOTES
    )
    const mealPrepNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP_NOTES
    )
    const groomingNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_NOTES
    )
    const skinCareApplicationNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_NOTES
    )
    const skinCareNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_NOTES
    )
    const romExerciseNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE_NOTES
    )
    const respiratoryCareNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE_NOTES
    )
    const oxygenTherapyNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY_NOTES
    )
    const oralSuctioningNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING_NOTES
    )
    const bowelFrequencyNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_NOTES
    )
    const bowelHygieneNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE_NOTES
    )
    const bowelEliminationNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_NOTES
    )
    const bowelProgramNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM_NOTES
    )
    const catheterCareNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE_NOTES
    )
    const mensesNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MENSES_NOTES
    )
    const urinaryHygieneNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE_NOTES
    )
    const positioningNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING_NOTES
    )
    const transfersNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS_NOTES
    )
    const vitalsPhysicianNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_NOTES
    )
    const vitalsNurseNotes = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE_NOTES
    )

    return {
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_QUESTION]: bathing,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_NOTES]: bathingNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION]:
        bracesSplintsStockings,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_NOTES]:
        bracesSplintsStockingsNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL]:
        bracesSplintsStockingsAdditional,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_QUESTION]: dressing,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_NOTES]: dressingNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_QUESTION]: eating,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_NOTES]: eatingNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION]: tubeFeedings,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_NOTES]: tubeFeedingsNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_QUESTION]: medication,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_NOTES]: medicationNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_QUESTION]: mobility,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_NOTES]: mobilityNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL]: mobilityAdditional,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION]: mealPrep,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_NOTES]: mealPrepNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_QUESTION]: grooming,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_NOTES]: groomingNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL]: groomingAdditional,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION]:
        skinCareApplication,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_NOTES]:
        skinCareApplicationNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL]:
        skinCareApplicationAdditional,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION]: skinCare,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_NOTES]: skinCareNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL]: skinCareAdditional,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION]: romExercise,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_NOTES]: romExerciseNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION]: respiratoryCare,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_NOTES]: respiratoryCareNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION]: oxygenTherapy,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_NOTES]: oxygenTherapyNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION]: oralSuctioning,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_NOTES]: oralSuctioningNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION]: bowelFrequency,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_NOTES]: bowelFrequencyNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION]: bowelHygiene,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_NOTES]: bowelHygieneNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION]: bowelElimination,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_NOTES]: bowelEliminationNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION]: bowelProgram,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_NOTES]: bowelProgramNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION]: catheterCare,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_NOTES]: catheterCareNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_QUESTION]: menses,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_NOTES]: mensesNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION]: urinaryHygiene,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_NOTES]: urinaryHygieneNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_QUESTION]: positioning,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_NOTES]: positioningNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_QUESTION]: transfers,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_NOTES]: transfersNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION]: vitalsPhysician,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_NOTES]: vitalsPhysicianNotes,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION]: vitalsNurse,
      [CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_NOTES]: vitalsNurseNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build coding value mutations using helper method
    const bathingMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BATHING,
      false
    )
    if (bathingMutation) {
      mutations.push(bathingMutation)
    }

    const bracesSplintsStockingsMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS,
      false
    )
    if (bracesSplintsStockingsMutation) {
      mutations.push(bracesSplintsStockingsMutation)
    }

    const bracesSplintsStockingsAdditionalMutation = this.buildCodingArrayMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_ADDITIONAL,
      false
    )
    if (bracesSplintsStockingsAdditionalMutation) {
      mutations.push(bracesSplintsStockingsAdditionalMutation)
    }

    const dressingMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING,
      false
    )
    if (dressingMutation) {
      mutations.push(dressingMutation)
    }

    const eatingMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_EATING,
      false
    )
    if (eatingMutation) {
      mutations.push(eatingMutation)
    }

    const tubeFeedingsMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS,
      false
    )
    if (tubeFeedingsMutation) {
      mutations.push(tubeFeedingsMutation)
    }

    const medicationMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION,
      false
    )
    if (medicationMutation) {
      mutations.push(medicationMutation)
    }

    const mobilityMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY,
      false
    )
    if (mobilityMutation) {
      mutations.push(mobilityMutation)
    }

    const mobilityAdditionalMutation = this.buildCodingArrayMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_ADDITIONAL,
      false
    )
    if (mobilityAdditionalMutation) {
      mutations.push(mobilityAdditionalMutation)
    }

    const mealPrepMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP,
      false
    )
    if (mealPrepMutation) {
      mutations.push(mealPrepMutation)
    }

    const groomingMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING,
      false
    )
    if (groomingMutation) {
      mutations.push(groomingMutation)
    }

    const groomingAdditionalMutation = this.buildCodingArrayMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_ADDITIONAL,
      false
    )
    if (groomingAdditionalMutation) {
      mutations.push(groomingAdditionalMutation)
    }

    const skinCareApplicationMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION,
      false
    )
    if (skinCareApplicationMutation) {
      mutations.push(skinCareApplicationMutation)
    }

    const skinCareApplicationAdditionalMutation = this.buildCodingArrayMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_ADDITIONAL,
      false
    )
    if (skinCareApplicationAdditionalMutation) {
      mutations.push(skinCareApplicationAdditionalMutation)
    }

    const skinCareMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE,
      false
    )
    if (skinCareMutation) {
      mutations.push(skinCareMutation)
    }

    const skinCareAdditionalMutation = this.buildCodingArrayMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_ADDITIONAL,
      false
    )
    if (skinCareAdditionalMutation) {
      mutations.push(skinCareAdditionalMutation)
    }

    const romExerciseMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE,
      false
    )
    if (romExerciseMutation) {
      mutations.push(romExerciseMutation)
    }

    const respiratoryCareMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE,
      false
    )
    if (respiratoryCareMutation) {
      mutations.push(respiratoryCareMutation)
    }

    const oxygenTherapyMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY,
      false
    )
    if (oxygenTherapyMutation) {
      mutations.push(oxygenTherapyMutation)
    }

    const oralSuctioningMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING,
      false
    )
    if (oralSuctioningMutation) {
      mutations.push(oralSuctioningMutation)
    }

    const bowelFrequencyMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY,
      false
    )
    if (bowelFrequencyMutation) {
      mutations.push(bowelFrequencyMutation)
    }

    const bowelHygieneMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE,
      false
    )
    if (bowelHygieneMutation) {
      mutations.push(bowelHygieneMutation)
    }

    const bowelEliminationMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION,
      false
    )
    if (bowelEliminationMutation) {
      mutations.push(bowelEliminationMutation)
    }

    const bowelProgramMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM,
      false
    )
    if (bowelProgramMutation) {
      mutations.push(bowelProgramMutation)
    }

    const catheterCareMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE,
      false
    )
    if (catheterCareMutation) {
      mutations.push(catheterCareMutation)
    }

    const mensesMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MENSES,
      false
    )
    if (mensesMutation) {
      mutations.push(mensesMutation)
    }

    const urinaryHygieneMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE,
      false
    )
    if (urinaryHygieneMutation) {
      mutations.push(urinaryHygieneMutation)
    }

    const positioningMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING,
      false
    )
    if (positioningMutation) {
      mutations.push(positioningMutation)
    }

    const transfersMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS,
      false
    )
    if (transfersMutation) {
      mutations.push(transfersMutation)
    }

    const vitalsPhysicianMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN,
      false
    )
    if (vitalsPhysicianMutation) {
      mutations.push(vitalsPhysicianMutation)
    }

    const vitalsNurseMutation = this.buildSingleCodingValueMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_QUESTION,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE,
      false
    )
    if (vitalsNurseMutation) {
      mutations.push(vitalsNurseMutation)
    }

    // Build notes field mutations using helper method
    const bathingNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BATHING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BATHING_NOTES
    )
    if (bathingNotesMutation) {
      mutations.push(bathingNotesMutation)
    }

    const bracesSplintsStockingsNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BRACES_SPLINTS_STOCKINGS_NOTES
    )
    if (bracesSplintsStockingsNotesMutation) {
      mutations.push(bracesSplintsStockingsNotesMutation)
    }

    const dressingNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_DRESSING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_DRESSING_NOTES
    )
    if (dressingNotesMutation) {
      mutations.push(dressingNotesMutation)
    }

    const eatingNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_EATING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_EATING_NOTES
    )
    if (eatingNotesMutation) {
      mutations.push(eatingNotesMutation)
    }

    const tubeFeedingsNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_TUBE_FEEDINGS_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TUBE_FEEDINGS_NOTES
    )
    if (tubeFeedingsNotesMutation) {
      mutations.push(tubeFeedingsNotesMutation)
    }

    const medicationNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEDICATION_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEDICATION_NOTES
    )
    if (medicationNotesMutation) {
      mutations.push(medicationNotesMutation)
    }

    const mobilityNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MOBILITY_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MOBILITY_NOTES
    )
    if (mobilityNotesMutation) {
      mutations.push(mobilityNotesMutation)
    }

    const mealPrepNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MEAL_PREP_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MEAL_PREP_NOTES
    )
    if (mealPrepNotesMutation) {
      mutations.push(mealPrepNotesMutation)
    }

    const groomingNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_GROOMING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_GROOMING_NOTES
    )
    if (groomingNotesMutation) {
      mutations.push(groomingNotesMutation)
    }

    const skinCareApplicationNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_APPLICATION_NOTES
    )
    if (skinCareApplicationNotesMutation) {
      mutations.push(skinCareApplicationNotesMutation)
    }

    const skinCareNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_SKIN_CARE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_SKIN_CARE_NOTES
    )
    if (skinCareNotesMutation) {
      mutations.push(skinCareNotesMutation)
    }

    const romExerciseNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_ROM_EXERCISE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ROM_EXERCISE_NOTES
    )
    if (romExerciseNotesMutation) {
      mutations.push(romExerciseNotesMutation)
    }

    const respiratoryCareNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_RESPIRATORY_CARE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_RESPIRATORY_CARE_NOTES
    )
    if (respiratoryCareNotesMutation) {
      mutations.push(respiratoryCareNotesMutation)
    }

    const oxygenTherapyNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_OXYGEN_THERAPY_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_OXYGEN_THERAPY_NOTES
    )
    if (oxygenTherapyNotesMutation) {
      mutations.push(oxygenTherapyNotesMutation)
    }

    const oralSuctioningNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_ORAL_SUCTIONING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_ORAL_SUCTIONING_NOTES
    )
    if (oralSuctioningNotesMutation) {
      mutations.push(oralSuctioningNotesMutation)
    }

    const bowelFrequencyNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_FREQUENCY_NOTES
    )
    if (bowelFrequencyNotesMutation) {
      mutations.push(bowelFrequencyNotesMutation)
    }

    const bowelHygieneNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_HYGIENE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_HYGIENE_NOTES
    )
    if (bowelHygieneNotesMutation) {
      mutations.push(bowelHygieneNotesMutation)
    }

    const bowelEliminationNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_ELIMINATION_NOTES
    )
    if (bowelEliminationNotesMutation) {
      mutations.push(bowelEliminationNotesMutation)
    }

    const bowelProgramNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_BOWEL_PROGRAM_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_BOWEL_PROGRAM_NOTES
    )
    if (bowelProgramNotesMutation) {
      mutations.push(bowelProgramNotesMutation)
    }

    const catheterCareNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_CATHETER_CARE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_CATHETER_CARE_NOTES
    )
    if (catheterCareNotesMutation) {
      mutations.push(catheterCareNotesMutation)
    }

    const mensesNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_MENSES_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_MENSES_NOTES
    )
    if (mensesNotesMutation) {
      mutations.push(mensesNotesMutation)
    }

    const urinaryHygieneNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_URINARY_HYGIENE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_URINARY_HYGIENE_NOTES
    )
    if (urinaryHygieneNotesMutation) {
      mutations.push(urinaryHygieneNotesMutation)
    }

    const positioningNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_POSITIONING_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_POSITIONING_NOTES
    )
    if (positioningNotesMutation) {
      mutations.push(positioningNotesMutation)
    }

    const transfersNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_TRANSFERS_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_TRANSFERS_NOTES
    )
    if (transfersNotesMutation) {
      mutations.push(transfersNotesMutation)
    }

    const vitalsPhysicianNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_PHYSICIAN_NOTES
    )
    if (vitalsPhysicianNotesMutation) {
      mutations.push(vitalsPhysicianNotesMutation)
    }

    const vitalsNurseNotesMutation = this.buildNotesFieldMutation(
      CNASkilledTasksFormField.CNA_SKILLED_TASKS_VITALS_NURSE_NOTES,
      PatAssessmentQuestionnaireItem.CNA_SKILLED_TASKS_VITALS_NURSE_NOTES
    )
    if (vitalsNurseNotesMutation) {
      mutations.push(vitalsNurseNotesMutation)
    }

    return mutations
  }
}
